import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { fetchAllCommercePartners } from "../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import appLogo from "../../assets/app/logo.png";

import {
  Search, X, ChevronRight, ChevronDown, ChevronUp, Plus, Minus,
  ShoppingCart, MapPin, Globe, SlidersHorizontal, Package, Star, Heart,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════════
   UNIFIED ATTRIBUTES — single source of truth for colours / sizes.
   NOTE: replaces the old per-page Sizes / ShoeSizes / productColors import.
═══════════════════════════════════════════════════════════════════════ */
import {
  productColorAttributes,
  fashionSizeAttributes,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════════════════
   EVERY CATEGORY DOMAIN — adjust these paths if your project's file
   names differ, they're assumed to live next to FashionCategories.tsx
   and BeautyCategories.tsx in "src/CATEGORIES/".
═══════════════════════════════════════════════════════════════════════ */

/* FASHION — grown-up ranges only. Baby/Maternity is its own standalone
   vertical below (BabyCategories), same split AddProducts.tsx uses. */
import {
  GenderCategories,
  FashionCategories,
  FashionClothing, FashionAccessories, FashionSunglasses, FashionHats, FashionWatches,
  FashionJewellery, FashionNecklaces, FashionRings, FashionShoes, FashionBags,
} from "../../CATEGORIES/FashionCategories";

import {
  BeautyCategories,
  BeautySkincareSubcategories, AllBeautyProducts,
  BeautyMakeupSubcategories, BeautyHaircareSubcategories, BeautyFragranceSubcategories,
  BeautyBodySubcategories, BeautyNailsSubcategories, BeautyToolsSubcategories,
  BeautyMensGroomingSubcategories, BeautyWellnessSubcategories, BeautyKidsBabySubcategories,
  BeautySetsSubcategories,
} from "../../CATEGORIES/BeautyCategories";

/* MATERNITY — the full, standalone Baby & Maternity tree (mirrors the
   import block in AddProducts.tsx). This was previously (incorrectly)
   sourced from FashionCategories' baby exports; those ranges now live
   here instead, so this page and the listing form stay in sync. */
import {
  BabyMaternityCategories,
  BabyClothing as MaternityBabyClothing,
  BabyShoes as MaternityBabyShoes,
  BabyCare as MaternityBabyCare,
  MommyCare as MaternityMommyCare,
  BabyFeeding as MaternityBabyFeeding,
  BabyBedtime as MaternityBabyBedtime,
  BabyTravel as MaternityBabyTravel,
  BabyToys as MaternityBabyToys,
  BabyNursery as MaternityBabyNursery,
  Maternity as MaternityWear,
  BabyHealthSafety as MaternityHealthSafety,
  ActivityEntertainment as MaternityActivity,
  BabyAccessories as MaternityAccessories,
  PottyTraining as MaternityPottyTraining,
  Gifts as MaternityGifts,
  BabyStationery as MaternityStationery,
  CarSeatsAccessories as MaternityCarSeats,
  Clothing as MaternityClothingRange,
  BabyTeething as MaternityTeething,
  AllBabyMaternityProducts,
} from "../../CATEGORIES/BabyCategories";

import {
  idArtCraftsCategories, idArtCraftsSubcategories, idArtCraftsProducts,
} from "../../CATEGORIES/ArtCraftsCategories";

import {
  automotiveCategories, automotiveSubcategories, automotiveProducts,
} from "../../CATEGORIES/AutomotiveCategories";

import {
  idAviationCategories, idAviationSubcategories, idAviationProducts,
} from "../../CATEGORIES/AviationCategories";

import {
  idBooksCategories, idBooksSubcategories, idBooksProducts,
} from "../../CATEGORIES/BooksCategories";

import {
  electronicsCategories, electronicsSubcategories, electronicsProducts,
} from "../../CATEGORIES/ElectronicsCategories";

/* FARM — was entirely missing from this page even though it exists as
   a full category group in AddProducts.tsx (farmCategories/farmSubs/
   farmProducts + its own icon renderer). */
import {
  farmCategories, farmSubcategories, farmProducts, renderFarmIcon,
} from "../../CATEGORIES/FarmCategories";

import {
  idFurnitureCategories, idFurnitureSubcategories, idFurnitureProducts,
} from "../../CATEGORIES/FurnitureCategories";

import {
  idGamingCategories, idGamingSubcategories, idGamingProducts,
} from "../../CATEGORIES/GamingCategories";

/* GIFTS — same story as Farm: fully absent here before. */
import {
  giftsCategories, giftsSubcategories, giftsProducts, renderGiftsIcon,
} from "../../CATEGORIES/GiftsCategories";

import {
  groceryCategories, grocerySubcategories, groceryProducts, renderGroceryIcon,
} from "../../CATEGORIES/GroceryCategories";

import {
  kitchenCategories, kitchenSubcategories, kitchenProducts,
} from "../../CATEGORIES/KitchenCategories";

import {
  idLuggageCategories, idLuggageSubcategories, idLuggageProducts,
} from "../../CATEGORIES/LuggageCategories";

import {
  idPetsCategories, idPetsSubcategories, idPetsProducts,
} from "../../CATEGORIES/PetsCategories";

import {
  sportsCategories, sportsSubcategories, sportsProducts,
} from "../../CATEGORIES/SportsCategories";

import {
  toolsCategories, toolsSubcategories, toolsProducts,
} from "../../CATEGORIES/ToolsCategories";

import {
  idToysCategories, idToysSubcategories, idToysProducts,
} from "../../CATEGORIES/ToysCategories";

/* ═══════════════════════════════════════════════════════════════════════
   sphere helper for the colour-swatch variant picker
═══════════════════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.28) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════════════════
   DESIGN TOKENS — HEARTLAS PALETTE
   Matches AtlasHome.tsx / FarmHome.tsx exactly (deep crimson red, warm
   gold, near-black outline, cream page background) instead of the old
   orange/coral theme this page used to carry on its own.
═══════════════════════════════════════════════════════════════════════ */
const F = {
  pageBg:   "#FBF6EE",
  cardBg:   "#FFFFFF",
  headerBg: "#9E1521",

  ink:      "#1B0E06",
  muted:    "#7A6653",
  mutedDim: "#A6947F",

  orange:   "#F2A93E", // gold
  red:      "#C8202E",
  cream:    "#FCEFD4",
  peach:    "#F7D9A0",
  peachDim: "rgba(242,169,62,0.10)",

  border:   "#EEDFC4",

  grad:     "linear-gradient(135deg,#C8202E 0%,#9E1521 100%)",
  gradH:    "linear-gradient(90deg,#F2A93E 0%,#C8202E 100%)",

  outline:  "1.5px solid #EEDFC4",
  sidebarBg:"#FFFFFF",
};

const RGBA_DARK = (a: number) => `rgba(27,14,6,${a})`;
const RGBA_GOLD = (a: number) => `rgba(242,169,62,${a})`;
const RGBA_RED  = (a: number) => `rgba(200,32,46,${a})`;

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  input,button{font-family:inherit;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.35}}

  input::placeholder{color:rgba(255,255,255,.6);}

  .f-card{transition:box-shadow .22s,transform .22s;}
  .f-card:hover{
    box-shadow:0 0 0 1.5px ${RGBA_RED(0.30)},0 14px 36px ${RGBA_GOLD(0.16)};
    transform:translateY(-4px);
  }

  .price-pill{
    display:inline-flex;align-items:center;
    background:linear-gradient(90deg,#F2A93E,#C8202E);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    border-radius:999px;padding:4px 14px;
    font-weight:900;color:#fff;letter-spacing:-0.4px;
    box-shadow:0 3px 12px ${RGBA_RED(0.32)};
  }

  .add-btn{
    border-radius:50%;border:none;cursor:pointer;
    background:linear-gradient(135deg,#F2A93E,#C8202E);
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 3px 14px ${RGBA_RED(0.35)};
    transition:transform .18s,box-shadow .18s;
  }
  .add-btn:hover{transform:scale(1.12);box-shadow:0 6px 22px ${RGBA_RED(0.5)};}
  .add-btn:active{transform:scale(0.88);}

  .sort-item{
    padding:11px 18px;font-size:13px;cursor:pointer;
    display:flex;align-items:center;justify-content:space-between;
    border-bottom:1px solid ${RGBA_DARK(0.08)};
    color:${F.ink};font-weight:600;transition:background .15s;
  }
  .sort-item:hover{background:${F.peachDim};}
  .sort-item.active{color:${F.red};font-weight:800;}

  .h-btn{
    display:flex;align-items:center;gap:6px;
    background:rgba(255,255,255,.16);
    border:1px solid rgba(255,255,255,.28);
    cursor:pointer;color:#fff;
    font-size:12px;font-weight:700;
    padding:8px 14px;border-radius:999px;
    flex-shrink:0;white-space:nowrap;
    transition:background .2s;
    font-family:inherit;
  }
  .h-btn:hover{background:rgba(255,255,255,.26);}

  .pill-btn{
    display:flex;align-items:center;gap:6px;
    border-radius:999px;font-size:11px;font-weight:700;
    cursor:pointer;font-family:inherit;border:1.5px solid ${RGBA_GOLD(0.22)};
    background:#fff;color:${F.muted};padding:7px 13px;transition:all .18s;
  }
  .pill-btn.active{background:linear-gradient(90deg,#F2A93E,#C8202E);color:#fff;border-color:transparent;}
`;

/* ═══════════════════════════════════════════════════════════════════════
   UNIFIED CATEGORY MODEL
   Every domain file ships items shaped slightly differently (icon as a
   ready JSX element vs. icon as a component reference, image vs none,
   color accent vs none). This normalises them into one tree so the page
   never has to special-case a domain again.
═══════════════════════════════════════════════════════════════════════ */
export interface UnifiedCategoryItem {
  id: number;
  name: string;
  icon?: React.ReactNode | React.ComponentType<{ size?: number; color?: string }>;
  parentId?: number | null;
  image?: any;
  color?: string;
  superCategoryId?: number;
}

/* Recognise forwardRef / memo component objects (e.g. some icon sets
   ship these instead of plain function components) without accidentally
   trying to render a random plain object as a component — that mismatch
   is what caused "Element type is invalid: ... but got: object" when a
   category's `icon` wasn't actually a component/element. Anything that
   doesn't match a known-renderable shape safely falls back to Package. */
const REACT_COMPONENT_TYPEOFS = new Set([
  Symbol.for("react.forward_ref"),
  Symbol.for("react.memo"),
]);

const isRenderableComponent = (icon: unknown): icon is React.ComponentType<{ size?: number; color?: string }> => {
  if (typeof icon === "function") return true;
  if (typeof icon === "object" && icon !== null && "$$typeof" in (icon as any)) {
    return REACT_COMPONENT_TYPEOFS.has((icon as any).$$typeof);
  }
  return false;
};

const renderIcon = (icon: UnifiedCategoryItem["icon"], size = 16, color?: string) => {
  if (!icon) return <Package size={size} color={color} />;
  if (React.isValidElement(icon)) return React.cloneElement(icon as any, { size, color });
  if (isRenderableComponent(icon)) {
    const IconComp = icon as React.ComponentType<{ size?: number; color?: string }>;
    return <IconComp size={size} color={color} />;
  }
  // Unknown/unsupported shape (e.g. a plain data object) — never attempt
  // to render it as a JSX type, just fall back to a safe default icon.
  return <Package size={size} color={color} />;
};


/** Clothing subcategory list, adults only — Onesies/Rompers/Baby Suits
 *  are baby items and don't belong under the general Clothing node. */
const FASHION_CLOTHING_ADULT = FashionClothing.filter(
  (c) => ![1101, 1102, 1103].includes(c.id)
);

/** Gender quick-filter options, adults only — no Babies. */
const ADULT_GENDER_CATEGORIES = GenderCategories.filter((g) => g.name !== "Babies");

/** Every category / subcategory / product-group across every domain, flattened. */
const ALL_CATEGORY_ITEMS: UnifiedCategoryItem[] = [
  ...GenderCategories,
  ...FashionCategories,
  ...FASHION_CLOTHING_ADULT, ...FashionAccessories, ...FashionSunglasses, ...FashionHats, ...FashionWatches,
  ...FashionJewellery, ...FashionNecklaces, ...FashionRings, ...FashionShoes, ...FashionBags,

  /* Maternity — standalone tree, same as AddProducts.tsx */
  ...BabyMaternityCategories,
  ...MaternityBabyClothing, ...MaternityBabyShoes, ...MaternityBabyCare, ...MaternityMommyCare,
  ...MaternityBabyFeeding, ...MaternityBabyBedtime, ...MaternityBabyTravel, ...MaternityBabyToys,
  ...MaternityBabyNursery, ...MaternityWear, ...MaternityHealthSafety, ...MaternityActivity,
  ...MaternityAccessories, ...MaternityPottyTraining, ...MaternityGifts, ...MaternityStationery,
  ...MaternityCarSeats, ...MaternityClothingRange, ...MaternityTeething,

  ...BeautyCategories,
  ...BeautySkincareSubcategories, ...BeautyMakeupSubcategories, ...BeautyHaircareSubcategories,
  ...BeautyFragranceSubcategories, ...BeautyBodySubcategories, ...BeautyNailsSubcategories,
  ...BeautyToolsSubcategories, ...BeautyMensGroomingSubcategories, ...BeautyWellnessSubcategories,
  ...BeautyKidsBabySubcategories, ...BeautySetsSubcategories, ...AllBeautyProducts,

  ...idArtCraftsCategories, ...idArtCraftsSubcategories, ...idArtCraftsProducts,
  ...automotiveCategories, ...automotiveSubcategories, ...automotiveProducts,
  ...idAviationCategories, ...idAviationSubcategories, ...idAviationProducts,
  ...idBooksCategories, ...idBooksSubcategories, ...idBooksProducts,
  ...electronicsCategories, ...electronicsSubcategories, ...electronicsProducts,

  /* Farm — previously missing entirely */
  ...farmCategories, ...farmSubcategories, ...farmProducts,

  ...idFurnitureCategories, ...idFurnitureSubcategories, ...idFurnitureProducts,
  ...idGamingCategories, ...idGamingSubcategories, ...idGamingProducts,

  /* Gifts — previously missing entirely */
  ...giftsCategories, ...giftsSubcategories, ...giftsProducts,

  ...groceryCategories, ...grocerySubcategories, ...groceryProducts,
  ...kitchenCategories, ...kitchenSubcategories, ...kitchenProducts,
  ...idLuggageCategories, ...idLuggageSubcategories, ...idLuggageProducts,
  ...idPetsCategories, ...idPetsSubcategories, ...idPetsProducts,
  ...sportsCategories, ...sportsSubcategories, ...sportsProducts,
  ...toolsCategories, ...toolsSubcategories, ...toolsProducts,
  ...idToysCategories, ...idToysSubcategories, ...idToysProducts,
] as UnifiedCategoryItem[];

const CATEGORY_ID_MAP = new Map<number, UnifiedCategoryItem>();
const CATEGORY_CHILDREN_MAP = new Map<number, UnifiedCategoryItem[]>();
ALL_CATEGORY_ITEMS.forEach((item) => CATEGORY_ID_MAP.set(item.id, item));
ALL_CATEGORY_ITEMS.forEach((item) => {
  if (item.parentId != null) {
    const arr = CATEGORY_CHILDREN_MAP.get(item.parentId) ?? [];
    arr.push(item);
    CATEGORY_CHILDREN_MAP.set(item.parentId, arr);
  }
});

/* ── Domain-specific icon renderers (Farm / Gifts / Grocery ship their
   own icon-resolution function rather than a plain icon field/component,
   same pattern AddProducts.tsx uses via `domainIconRenderer`). We build
   id lookup sets so the generic tree renderer knows when to defer to
   them instead of the generic renderIcon(). ── */
const FARM_ITEM_IDS    = new Set([...farmCategories, ...farmSubcategories, ...farmProducts].map((i: any) => i.id));
const GIFTS_ITEM_IDS   = new Set([...giftsCategories, ...giftsSubcategories, ...giftsProducts].map((i: any) => i.id));
const GROCERY_ITEM_IDS = new Set([...groceryCategories, ...grocerySubcategories, ...groceryProducts].map((i: any) => i.id));

type DomainIconRenderer = (item: any, size: number) => React.ReactNode;
const asDomainIconRenderer = (fn: unknown): DomainIconRenderer | undefined =>
  typeof fn === "function" ? (fn as unknown as DomainIconRenderer) : undefined;

const renderFarmIconFn    = asDomainIconRenderer(renderFarmIcon);
const renderGiftsIconFn   = asDomainIconRenderer(renderGiftsIcon);
const renderGroceryIconFn = asDomainIconRenderer(renderGroceryIcon);

function resolveItemIcon(item: UnifiedCategoryItem, size: number, color?: string): React.ReactNode {
  try {
    if (FARM_ITEM_IDS.has(item.id) && renderFarmIconFn) {
      const rendered = renderFarmIconFn(item, size);
      if (rendered) return <span style={{ display: "flex", alignItems: "center", color }}>{rendered}</span>;
    }
    if (GIFTS_ITEM_IDS.has(item.id) && renderGiftsIconFn) {
      const rendered = renderGiftsIconFn(item, size);
      if (rendered) return <span style={{ display: "flex", alignItems: "center", color }}>{rendered}</span>;
    }
    if (GROCERY_ITEM_IDS.has(item.id) && renderGroceryIconFn) {
      const rendered = renderGroceryIconFn(item, size);
      if (rendered) return <span style={{ display: "flex", alignItems: "center", color }}>{rendered}</span>;
    }
  } catch {
    // domain renderer threw / returned something unrenderable — fall through to the safe default below
  }
  return renderIcon(item.icon, size, color);
}

/** ids of Clothing / Accessories / Jewellery / Shoes / Bags — the only
 *  place a gender quick-filter makes sense. */
const FASHION_TYPE_IDS = new Set(FashionCategories.map((c) => c.id));

function getTopAncestor(id: number): UnifiedCategoryItem | undefined {
  let current = CATEGORY_ID_MAP.get(id);
  const seen = new Set<number>();
  while (current?.parentId != null && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = CATEGORY_ID_MAP.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return current;
}

function getDescendantIds(rootId: number): Set<number> {
  const set = new Set<number>([rootId]);
  const queue: number[] = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    (CATEGORY_CHILDREN_MAP.get(id) ?? []).forEach((c) => {
      if (!set.has(c.id)) { set.add(c.id); queue.push(c.id); }
    });
  }
  return set;
}

/* ═══════════════════════════════════════════════════════════════════════
   ATTRIBUTE LOOKUPS — unified source
═══════════════════════════════════════════════════════════════════════ */
const colorPalette = productColorAttributes.values ?? [];
const sizePalette   = fashionSizeAttributes.values ?? [];

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Recommended"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* ═══════════════════════════════════════════════════════════════════════
   LAYOUT TOKENS
═══════════════════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: F.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif" },
  headerTop:   { background: F.headerBg, backgroundImage: F.grad, position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.32)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 16px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: "rgba(255,255,255,.22)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  sortMenu:    { position: "fixed" as const, background: "#fff", borderRadius: 14, boxShadow: `0 20px 56px ${RGBA_RED(0.25)}`, zIndex: 9999, minWidth: 210, overflow: "hidden", border: F.outline },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  sidebar:     { background: F.sidebarBg, borderRadius: 20, padding: 18, boxShadow: `0 4px 24px ${RGBA_GOLD(0.10)}`, position: "sticky" as const, top: 96, maxHeight: "calc(100vh - 116px)", overflowY: "auto" as const, border: F.outline },
};

/* ═══════════════════════════════════════════════════════════════════════
   TICKER
═══════════════════════════════════════════════════════════════════════ */
const CategoryTicker: React.FC<{ denied: boolean; label: string }> = ({ denied, label }) => {
  const msg = denied
    ? `⚠ LOCATION DENIED — SHOWING ALL ${label.toUpperCase()} · ENABLE LOCATION FOR LOCAL BRANDS · `
    : `◉ BROWSING WORLDWIDE ${label.toUpperCase()} · SWITCH TO LOCAL TO FIND NEARBY BRANDS · `;
  return (
    <div style={{ overflow: "hidden", background: F.peachDim, borderBottom: `1px solid ${RGBA_GOLD(0.22)}`, height: 22, display: "flex", alignItems: "center" }}>
      <span style={{ display: "inline-block", whiteSpace: "nowrap", animation: "mc-ticker 26s linear infinite", fontSize: 9, fontWeight: 700, letterSpacing: ".18em", color: F.red, paddingLeft: 24 }}>
        {msg.repeat(6)}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════ */
const CategoryDetails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params   = useParams<{ categoryId: string }>();
  const { rate, symbol } = useContext(CurrencyContext);

  const categoryId    = Number(params.categoryId);
  const currentCategory = CATEGORY_ID_MAP.get(categoryId);
  const categoryLabel  = currentCategory?.name ?? "Products";
  const topAncestor    = useMemo(() => getTopAncestor(categoryId), [categoryId]);
  const isFashionDomain = topAncestor ? FASHION_TYPE_IDS.has(topAncestor.id) : false;

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId;

  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const ratingSummaries  = useSelector((s: RootState) => s.productRatings.summaries);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile,       setIsMobile]       = useState(window.innerWidth <= 768);
  const [catOpen,        setCatOpen]        = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(true);
  const [searchQuery,    setSearchQuery]    = useState("");
  const [searchFocused,  setSearchFocused]  = useState(false);
  const [sortOpen,       setSortOpen]       = useState(false);
  const [activeSort,     setActiveSort]     = useState<SortKey>("default");
  const [activeFilterId, setActiveFilterId] = useState<number | null>(null);
  const [activeGenderId, setActiveGenderId] = useState<number | null>(null);
  const [openIds,        setOpenIds]        = useState<Set<number>>(new Set());

  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,     setUserCountry]     = useState<string | null>(null);
  const [userCity,        setUserCity]        = useState<string | null>(null);
  const [userLat,         setUserLat]         = useState<number | null>(null);
  const [locationDenied,  setLocationDenied]  = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showGlobal,      setShowGlobal]      = useState(false);
  const locationActive = !locationDenied && userLat !== null;
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  const [wished, setWished] = useState<Set<string>>(new Set());

  /* ── on category change: reset filters and default-open the first
        subcategory so the tree never lands on an empty accordion,
        and browsing "just works" even if products are only tagged
        deep in the tree rather than on the top-level id ── */
  useEffect(() => {
    setActiveFilterId(null); setActiveGenderId(null);
    setSearchQuery(""); setActiveSort("default");
    const kids = CATEGORY_CHILDREN_MAP.get(categoryId) ?? [];
    setOpenIds(kids.length ? new Set([categoryId, kids[0].id]) : new Set([categoryId]));
    window.scrollTo({ top: 0 });
  }, [categoryId]);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        err => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  const resetFilters = () => {
    setActiveFilterId(null); setActiveGenderId(null); setActiveSort("default");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  };

  useEffect(() => { if (searchQuery.trim()) setActiveFilterId(activeFilterId); }, [searchQuery]); // eslint-disable-line

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const hasVariants = (item: any) => item.variants && Object.keys(item.variants).length > 0;

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (hasVariants(item)) {
      setVariantItem(item); setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({ listingId: item.listingId, name: item.name, rate: item.rate, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId } as any));
  };

  const handleVariantAdd = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartBrandCompat(variantItem.brandId);
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name, rate: variantItem.rate,
      photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selectedColor ?? undefined, sizeId: selectedSize ?? undefined, qty: variantQty,
      hostId: variantItem.hostId, brandId: variantItem.brandId,
    } as any));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToDetails  = (id: string) => navigate(`/productdetails/${id}`);
  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, { state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.rate, 0), items: cartItems } });
  };

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCountry]);

  /* ── THE ONE GATE — works for every domain and every depth.
        A product matches if ANY of its tag fields (categoryId /
        subcategoryId / productId) falls inside the descendant set of
        whatever node is currently selected (or the route root if
        nothing more specific is picked). This is what makes "default
        to a subcategory when the top category alone has no items"
        work automatically — the top id's descendant set already
        includes every leaf under it. ── */
  const descendantIds = useMemo(
    () => getDescendantIds(activeFilterId ?? categoryId),
    [activeFilterId, categoryId]
  );

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (!categoryId) return false;

      const matchesBase =
        descendantIds.has(l.categoryId) ||
        descendantIds.has(l.subcategoryId) ||
        descendantIds.has(l.productId);
      if (!matchesBase) return false;

      if (activeGenderId && l.categoryId !== activeGenderId && l.subcategoryId !== activeGenderId) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });

    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return list;
  }, [filteredListings, categoryId, descendantIds, activeGenderId, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    return order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
  }, [filteredProducts]);

  

  /* ═════════════════════════════
     HEADER — logo (home link) + search + cart. Nothing else.
  ═════════════════════════════ */
  const Header = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 24px" }}>
      <div onClick={() => navigate(`/ProductsHome/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0, userSelect: "none" }}>
      </div>

      <div style={{ ...S.searchWrap, borderColor: searchFocused ? "#fff" : "rgba(255,255,255,.32)" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
          placeholder={`Search ${categoryLabel.toLowerCase()}…`} style={S.searchInput} />
        {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}><X size={14} /></button>}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <button onClick={goToCheckout} className="h-btn" style={{ position: "relative", background: cartTotal > 0 ? "rgba(255,255,255,.32)" : "rgba(255,255,255,.16)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: "#fff", color: F.red, fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
      </button>
    </div>
  );

  /* ═════════════════════════════
     RECURSIVE CATEGORY TREE — one component walks every domain,
     any depth, using the icon/image shape each domain shipped.
  ═════════════════════════════ */
  const CategoryNode: React.FC<{ item: UnifiedCategoryItem; depth: number }> = ({ item, depth }) => {
    const kids = CATEGORY_CHILDREN_MAP.get(item.id) ?? [];
    const isOpen = openIds.has(item.id);
    const isSelected = (activeFilterId ?? categoryId) === item.id;
    return (
      <div style={{ marginTop: depth === 0 ? 0 : 4 }}>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: depth === 0 ? "9px 10px" : "7px 8px",
            marginLeft: depth * 14,
            borderRadius: 10,
            background: isSelected ? F.peachDim : "transparent",
            border: `1px solid ${isSelected ? RGBA_RED(0.28) : "transparent"}`,
            cursor: "pointer",
          }}
          onClick={() => {
            setActiveFilterId(prev => (prev === item.id ? null : item.id));
            if (isMobile) setCatOpen(false);
          }}
        >
          <span style={{ display: "flex", flexShrink: 0, color: isSelected ? F.red : F.mutedDim }}>
            {item.image
              ? <img src={item.image} alt={item.name} style={{ width: 18, height: 18, objectFit: "contain" }} />
              : resolveItemIcon(item, 16, isSelected ? F.red : F.mutedDim)}
          </span>
          <span style={{ flex: 1, fontSize: depth === 0 ? 13.5 : 13, fontWeight: isSelected ? 800 : 600, color: isSelected ? F.red : F.ink }}>
            {item.name}
          </span>
          {isSelected && <span style={{ color: F.red, fontWeight: 900, fontSize: 12 }}>✓</span>}
          {kids.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenIds(prev => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; });
              }}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 2, flexShrink: 0 }}
            >
              <motion.span animate={{ rotate: isOpen ? 90 : 0 }} style={{ display: "flex" }}>
                <ChevronRight size={14} color={F.mutedDim} />
              </motion.span>
            </button>
          )}
        </div>
        <AnimatePresence initial={false}>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {kids.map(k => <CategoryNode key={k.id} item={k} depth={depth + 1} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <div style={{ borderRadius: 18, padding: 14, background: "#fff", border: F.outline, boxShadow: `0 2px 12px ${RGBA_GOLD(0.08)}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: F.mutedDim, marginBottom: 10 }}>
        BROWSE {categoryLabel.toUpperCase()}
      </div>
      {currentCategory
        ? <CategoryNode item={currentCategory} depth={0} />
        : <div style={{ fontSize: 12, color: F.mutedDim, padding: "8px 2px" }}>Category not found.</div>}
    </div>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.5), zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: RGBA_GOLD(0.3), borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${F.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 800, fontSize: 11, letterSpacing: "0.15em", color: F.red }}>{categoryLabel.toUpperCase()}</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={F.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═════════════════════════════
     CATEGORY BANNER — shows current node, tap opens the tree
     (this is the only category-browsing affordance left now that
     the header is search+cart only)
  ═════════════════════════════ */
  const CategorySwitchStrip = () => {
    if (!currentCategory) return null;
    return (
      <div
        onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v))}
        style={{ marginBottom: 20, padding: isMobile ? "14px 16px" : "16px 22px", background: F.gradH, borderRadius: 18, boxShadow: `0 8px 24px ${RGBA_RED(0.18)}`, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: "50%", overflow: "hidden",
            border: "2px solid #fff", boxShadow: "0 0 0 3px rgba(255,255,255,.35)",
            background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {currentCategory.image
              ? <img src={currentCategory.image} alt={currentCategory.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : resolveItemIcon(currentCategory, 22, "#fff")}
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: "#fff", display: "block" }}>{currentCategory.name}</span>
            {topAncestor && topAncestor.id !== currentCategory.id && (
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600 }}>in {topAncestor.name}</span>
            )}
          </div>
          <ChevronDown size={18} color="#fff" style={{ opacity: 0.85 }} />
        </div>
      </div>
    );
  };



  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Boutique";
    const shopLogo = partner?.image || appLogo;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${F.border}`, background: "#fff" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </div>
        <span style={{ fontWeight: 900, fontSize: 14, color: F.ink, textDecoration: "underline", textDecorationColor: F.orange, textDecorationThickness: 2, textUnderlineOffset: 4 }}>{shopName}</span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => navigate(`/StoreHome/${brandId}`)}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: `0 3px 10px ${RGBA_GOLD(0.18)}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={F.red} />
        </motion.button>
      </div>
    );
  };

  /* ═════════════════════════════
     PRODUCT CARD
  ═════════════════════════════ */
  const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const qty      = getQty(item.listingId);
    const price    = `${symbol || "€"}${((item.rate ?? 0) * (rate ?? 1)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const summary  = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const isWished = wished.has(item.listingId);

    return (
      <motion.div className="f-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 20, overflow: "hidden", background: F.cardBg, border: F.outline, cursor: "pointer", display: "flex", flexDirection: "column" }}
        onClick={() => goToDetails(item.listingId)}>
        <div style={{ width: "100%", aspectRatio: "3/4", position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${F.cream},${F.peach})` }}>
          {item.photos?.[0] ? (
            <motion.img src={item.photos[0]} alt={item.name}
              whileHover={{ scale: 1.08 }} transition={{ type: "spring", stiffness: 300, damping: 26 }}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={isMob ? 36 : 44} color={F.orange} style={{ opacity: 0.45 }} />
            </div>
          )}

          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${RGBA_DARK(0.72)} 0%,${RGBA_DARK(0.14)} 42%,transparent 68%)`, pointerEvents: "none" }} />

          {discount && (
            <div style={{ position: "absolute", top: 8, left: 8, background: F.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {item.isNewArrival && !discount && (
            <div style={{ position: "absolute", top: 8, left: 8, background: F.orange, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>NEW</div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", top: 8, right: isMob ? 50 : 56, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${F.border}`, borderRadius: 999, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={F.orange} color={F.orange} />
              <span style={{ color: F.ink, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
            </div>
          )}

          <button onClick={e => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,.9)", border: "none", width: isMob ? 30 : 34, height: isMob ? 30 : 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 7 }}>
            <Heart size={14} color={isWished ? F.red : F.muted} fill={isWished ? F.red : "none"} />
          </button>

          <div style={{ position: "absolute", bottom: 44, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                style={{ display: "flex", alignItems: "center", background: RGBA_RED(0.92), borderRadius: 999, padding: isMob ? "3px 5px" : "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.3)" }}>
                <button style={{ width: isMob ? 22 : 26, height: isMob ? 22 : 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                  <Minus size={isMob ? 8 : 10} color="#fff" />
                </button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: isMob ? 18 : 22, textAlign: "center", fontSize: isMob ? 11 : 13 }}>{qty}</span>
                <button style={{ width: isMob ? 22 : 26, height: isMob ? 22 : 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.32)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                  <Plus size={isMob ? 8 : 10} color="#fff" />
                </button>
              </motion.div>
            ) : (
              <motion.button className="add-btn" whileTap={{ scale: 0.88 }} style={{ width: isMob ? 36 : 42, height: isMob ? 36 : 42 }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <ShoppingCart size={isMob ? 14 : 17} color="#fff" />
              </motion.button>
            )}
          </div>

          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMob ? "8px 10px 10px" : "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: isMob ? 11 : 13, color: "#fff", lineHeight: 1.25, marginBottom: isMob ? 5 : 7, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 6px rgba(0,0,0,.5)" }}>
              {item.name}
            </div>
            <span className="price-pill" style={{ fontSize: isMob ? 13 : 17 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ═════════════════════════════
     RENDER PRODUCTS — sort + local/global controls live here now,
     since the header only carries search + cart.
  ═════════════════════════════ */
  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: F.gradH }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: F.ink }}>
            {activeFilterId ? CATEGORY_ID_MAP.get(activeFilterId)?.name ?? "Filtered" : locationDenied ? `All ${categoryLabel}` : showGlobal ? `Global ${categoryLabel}` : userCity ? `Near ${userCity}` : userCountry ?? `All ${categoryLabel}`}
          </span>
          <span style={{ fontSize: 12, color: F.muted, background: F.peachDim, padding: "2px 9px", borderRadius: 999 }}>{filteredProducts.length}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button ref={sortBtnRef} onClick={toggleSort} className="pill-btn" style={{ background: sortOpen ? F.peachDim : "#fff" }}>
            <SlidersHorizontal size={12} />
            {activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}
            {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {locationActive && (
            <button onClick={handleToggleGlobal} className="pill-btn" style={{ background: showGlobal ? "#fff" : F.peachDim, color: showGlobal ? F.mutedDim : F.red, borderColor: showGlobal ? F.border : RGBA_RED(0.3) }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
          {(activeFilterId || activeGenderId || activeSort !== "default") && (
            <button onClick={resetFilters} className="pill-btn"><X size={11} /> Clear</button>
          )}
        </div>

        {sortOpen && sortAnchor && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
            <div style={{ ...S.sortMenu, top: sortAnchor.top, left: sortAnchor.left }}>
              {SORT_OPTS.map(opt => (
                <div key={opt.key} className={`sort-item${activeSort === opt.key ? " active" : ""}`}
                  onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }}>
                  {opt.label}{activeSort === opt.key && <span style={{ fontWeight: 900 }}>✓</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {filteredProducts.length > 0 ? (
        productsByBrand.map(group => (
          <div key={group.brandId}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>✦</div>
          <h3 style={{ fontWeight: 900, color: F.ink, marginBottom: 8 }}>{locationDenied ? "Location needed" : `No ${categoryLabel.toLowerCase()} found`}</h3>
          <p style={{ color: F.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local brands." : "Try adjusting your filters."}</p>
          <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetFilters()}
            style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: F.gradH, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
            {locationDenied ? "Enable Location" : `View All ${categoryLabel}`}
          </button>
        </div>
      )}
    </div>
  );

  /* ═════════════════════════════
     VARIANT MODAL — colours/sizes from the unified attributes file
  ═════════════════════════════ */
  const VariantModal = () => {
    const variantColorIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const sizeIds: number[] = selectedColor && variantItem?.variants?.[selectedColor]?.sizeIds ? variantItem.variants[selectedColor].sizeIds : [];
    const previewImg = selectedColor && variantItem?.variants?.[selectedColor]?.imageUrl ? variantItem.variants[selectedColor].imageUrl : variantItem?.photos?.[0];
    const canContinue = variantColorIds.length === 0 || (selectedColor !== null && (sizeIds.length === 0 || selectedSize !== null));

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: F.gradH, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: RGBA_GOLD(0.22), margin: "12px auto 0", flexShrink: 0 }} />}
        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg && (
            <div style={{ width: "100%", aspectRatio: "16/7", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: F.cream }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: F.ink, marginBottom: 4 }}>{variantItem?.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, background: F.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 18 }}>
            {symbol}{((variantItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}
          </div>

          {variantColorIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: F.mutedDim, marginBottom: 12 }}>
                COLOUR
                {selectedColor && (() => {
                  const cv = colorPalette.find(c => c.id === selectedColor);
                  return cv ? <span style={{ fontWeight: 600, color: F.red, letterSpacing: "0.06em", textTransform: "none", fontSize: 12 }}> — {cv.name}</span> : null;
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(64px,1fr))", gap: 10, marginBottom: 20 }}>
                {variantColorIds.map(cId => {
                  const cv = colorPalette.find(c => c.id === cId);
                  const hex = cv?.hex ?? cv?.hexCodes?.[0];
                  const isSel = selectedColor === cId;
                  return (
                    <button key={cId} onClick={() => { setSelectedColor(cId); setSelectedSize(null); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                      <div style={{ position: "relative", width: 40, height: 40 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%",
                          background: hex ? sphere3D(hex) : sphere3D(F.orange),
                          boxShadow: `inset -6px -5px 12px rgba(0,0,0,.22),inset 5px 5px 10px rgba(255,255,255,.25),0 5px 18px ${RGBA_GOLD(0.18)}${isSel ? ",0 0 0 2.5px " + F.red : ""}`,
                          outline: isSel ? `2.5px solid ${F.red}` : "none", outlineOffset: 3,
                        }} />
                        {isSel && (
                          <div style={{ position: "absolute", bottom: -3, right: -3, width: 16, height: 16, borderRadius: "50%", background: F.red, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }}>
                            <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: isSel ? 700 : 500, color: isSel ? F.red : F.mutedDim, textAlign: "center", maxWidth: 60, wordBreak: "break-word" as const, lineHeight: 1.2 }}>{cv?.name ?? String(cId)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selectedColor && sizeIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: F.mutedDim, marginBottom: 12 }}>SIZE</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {sizeIds.map((sId: number) => {
                  const sz = sizePalette.find(s => s.id === sId);
                  const isSel = selectedSize === sId;
                  return (
                    <button key={sId} onClick={() => setSelectedSize(sId)}
                      style={{ padding: "8px 16px", border: `1.5px solid ${isSel ? F.red : F.border}`, background: isSel ? F.red : "#fff", color: isSel ? "#fff" : F.ink, fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 8, transition: "all .15s", fontFamily: "inherit" }}>
                      {sz?.name ?? sId}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: F.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${F.border}`, borderRadius: 12, overflow: "hidden", background: F.pageBg }}>
              <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: F.red, fontFamily: "inherit" }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: F.ink, borderLeft: `1px solid ${F.border}`, borderRight: `1px solid ${F.border}` }}>{variantQty}</div>
              <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: F.red, fontFamily: "inherit" }}>+</button>
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${F.border}` }}>
          <button disabled={!canContinue} onClick={() => handleVariantAdd(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${F.ink}`, background: "transparent", color: F.ink, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45, fontFamily: "inherit" }}>
            Add to Bag
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantAdd(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "none", background: canContinue ? F.gradH : "#E0D2C4", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {variantOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => { setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); }}
              style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.55), zIndex: 500, backdropFilter: "blur(6px)" }} />
            {isMobile ? (
              <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
                style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
            ) : (
              <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                  transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, pointerEvents: "all", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${RGBA_RED(0.28)}` }}>
                  {content}
                </motion.div>
              </div>
            )}
          </>
        )}
      </AnimatePresence>
    );
  };

  /* ═════════════════════════════
     LOCATION MODAL
  ═════════════════════════════ */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.6), zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: `0 30px 80px ${RGBA_RED(0.3)}`, textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: F.gradH, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: F.ink }}>Location Disabled</h3>
            <p style={{ color: F.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local brands.</p>
            <button onClick={() => setLocationModalOpen(false)}
              style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: F.gradH, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═════════════════════════════
     RENDER
  ═════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>

      <div style={S.headerTop}>

        <Header />
      </div>

      <MobileCategorySheet />

      {!isMobile && (
        <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "280px 1fr" : "1fr" }}>
          {desktopCatOpen && (
            <aside style={S.sidebar}>
              <CategoryPanelContent />
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            <CategorySwitchStrip />
            {renderProducts(false)}
          </main>
        </div>
      )}

      {isMobile && (
        <div style={{ padding: "14px 12px 0" }}>
          <CategorySwitchStrip />
          {renderProducts(true)}
        </div>
      )}

      <VariantModal />
      <LocationModal />
    </div>
  );
};

export default CategoryDetails;